import { useEffect, useState } from 'react';
import Button from 'src/components/ui/Button';
import { ConfettiIcon } from '@phosphor-icons/react';
import { useRightPanelControl } from 'src/hooks/useRightPanelControl';
import OnboardingContent from './OnboardingContent';
import Step from 'src/components/ui/Step';
import { useLocation } from 'react-router-dom';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import RightPanelShell from 'src/components/layouts/RightPanelShell';

export default function ReviewGuide() {
  const { hideOnboarding, setContent } = useRightPanelControl();
  const navigate = useTenantNavigate();
  const location = useLocation();

  const [steps, setSteps] = useState<{
    tour: 'incomplete' | 'loading' | 'complete';
    download: 'incomplete' | 'loading' | 'complete';
    upload: 'incomplete' | 'loading' | 'complete';
    review: 'incomplete' | 'loading' | 'complete';
    inspect: 'incomplete' | 'loading' | 'complete';
  }>({
    tour: 'loading',
    download: 'incomplete',
    upload: 'incomplete',
    review: 'incomplete',
    inspect: 'incomplete'
  });

  // Ensure underlying page is review home
  useEffect(() => {
    if (!location.pathname.includes('/review')) {
      navigate('/review');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tour completed auto-advance
  useEffect(() => {
    const completed = localStorage.getItem('innex-review-tour-completed') === 'true';
    if (completed) {
      setSteps((s) => ({ ...s, tour: 'complete', download: 'loading' }));
    }
  }, []);

  useEffect(() => {
    if (steps.tour !== 'loading') return;
    const id = setInterval(() => {
      const done = localStorage.getItem('innex-review-tour-completed') === 'true';
      if (done) {
        setSteps((s) => ({ ...s, tour: 'complete', download: 'loading' }));
        clearInterval(id);
      }
    }, 500);
    return () => clearInterval(id);
  }, [steps.tour]);

  // Download step polling
  useEffect(() => {
    if (steps.download !== 'loading') return;
    const id = setInterval(() => {
      const downloaded = localStorage.getItem('innex-review-sample-policy-downloaded') === 'true';
      if (downloaded) {
        setSteps((s) => ({ ...s, download: 'complete', upload: 'loading' }));
        clearInterval(id);
      }
    }, 500);
    return () => clearInterval(id);
  }, [steps.download]);

  // Upload step: auto-advance when navigated to a specific review page (e.g. /review/:id)
  useEffect(() => {
    if (steps.upload !== 'loading') return;
    const isOnReview = location.pathname.includes('/review/');
    if (isOnReview) {
      setSteps((s) => ({ ...s, upload: 'complete', review: 'loading' }));
    }
  }, [location.pathname, steps.upload]);

  // Review step: mark complete only when user clicks Start Review in DomainDetection
  useEffect(() => {
    if (steps.review !== 'loading') return;
    const id = setInterval(() => {
      const startClicked = localStorage.getItem('innex-review-start-clicked') === 'true';
      if (startClicked) {
        setSteps((s) => ({ ...s, review: 'complete', inspect: 'loading' }));
        clearInterval(id);
      }
    }, 400);
    return () => clearInterval(id);
  }, [steps.review]);

  // Inspect step: mark complete when a finding is interacted with
  useEffect(() => {
    if (steps.inspect !== 'loading') return;
    const id = setInterval(() => {
      const inspected = localStorage.getItem('innex-review-inspected-errors') === 'true';
      if (inspected) {
        setSteps((s) => ({ ...s, inspect: 'complete' }));
        clearInterval(id);
      }
    }, 500);
    return () => clearInterval(id);
  }, [steps.inspect]);

  const backToOnboarding = () => {
    setContent(<OnboardingContent onComplete={hideOnboarding} />);
  };

  const header = (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        className="text-sm text-secondary-blue hover:underline self-start"
        onClick={backToOnboarding}
      >
        ← Back to all modules
      </button>
      <h2 className="text-lg font-semibold text-content-default">Review Assistant</h2>
    </div>
  );

  const allComplete =
    steps.tour === 'complete' &&
    steps.download === 'complete' &&
    steps.upload === 'complete' &&
    steps.review === 'complete' &&
    steps.inspect === 'complete';

  const skipStep = () => {
    setSteps((s) => {
      const order = ['tour', 'download', 'upload', 'review', 'inspect'] as const;
      const loadingIdx = order.findIndex((k) => s[k] === 'loading');
      const nextState = { ...s } as typeof s;
      if (loadingIdx >= 0) {
        nextState[order[loadingIdx]] = 'incomplete';
        const relNextIdx = order.slice(loadingIdx + 1).findIndex((k) => s[k] === 'incomplete');
        if (relNextIdx >= 0) {
          const idx = loadingIdx + 1 + relNextIdx;
          nextState[order[idx]] = 'loading';
        }
        return nextState;
      }
      const firstIncompleteIdx = order.findIndex((k) => s[k] === 'incomplete');
      if (firstIncompleteIdx >= 0) {
        nextState[order[firstIncompleteIdx]] = 'loading';
        return nextState;
      }
      return s;
    });
  };

  const footer = allComplete ? (
    <Button
      variant="primary"
      size="md"
      className="w-full"
      onClick={() => {
        try {
          localStorage.setItem('innex-onboarding-module-review-completed', 'true');
        } catch {}
        hideOnboarding();
      }}
    >
      Mark as complete
    </Button>
  ) : (
    <div className="flex flex-col gap-2">
      <Button variant="outline" size="md" className="w-full" onClick={skipStep}>
        Skip Step
      </Button>
      <Button variant="outline" size="md" className="w-full" onClick={() => navigate('/tutorials/review-assistant')}>
        Watch Review Tutorials
      </Button>
    </div>
  );

  return (
    <RightPanelShell header={header} footer={footer}>
      <div className="flex flex-col gap-2">
        {(() => {
          const setActiveStep = (key: 'tour' | 'download' | 'upload' | 'review' | 'inspect') => {
            setSteps((prev) => {
              if (prev[key] === 'loading') return prev;
              const next = { ...prev } as typeof prev;
              if (next.tour === 'loading') next.tour = 'incomplete';
              if (next.download === 'loading') next.download = 'incomplete';
              if (next.upload === 'loading') next.upload = 'incomplete';
              if (next.review === 'loading') next.review = 'incomplete';
              if (next.inspect === 'loading') next.inspect = 'incomplete';
              next[key] = 'loading';
              return next;
            });
          };

          return (
            <>
              <Step
                title="Complete Tour"
                state={steps.tour}
                onTitleClick={() => {
                  try {
                    localStorage.removeItem('innex-review-tour-completed');
                  } catch {}
                  setActiveStep('tour');
                }}
              >
                <div className="flex flex-col gap-2">
                  <div className="text-sm text-content-subtle">
                    Start our short guided tour to learn the Review Assistant.
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      try {
                        window.dispatchEvent(new Event('start-review-tour'));
                      } catch {}
                    }}
                  >
                    Start tour
                  </Button>
                </div>
              </Step>

              <Step
                title="Download Test Policy"
                state={steps.download}
                onTitleClick={() => {
                  try {
                    localStorage.removeItem('innex-review-sample-policy-downloaded');
                  } catch {}
                  setActiveStep('download');
                }}
              >
                {steps.download === 'loading' ? (
                  <div className="flex flex-col gap-2">
                    <div className="text-sm text-content-subtle">
                      Download the sample policy to use in the next step.
                    </div>
                    <Button
                      onClick={async () => {
                        try {
                          try {
                            localStorage.setItem('innex-review-sample-policy-downloaded', 'true');
                          } catch {}
                          setSteps((s) => ({ ...s, download: 'complete', upload: 'loading' }));

                          const response = await fetch(
                            'https://innexpublic.blob.core.windows.net/platform-assets/onboarding/Waste Policy.pdf'
                          );
                          if (!response.ok) throw new Error('Failed to fetch');
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = 'Waste Policy.pdf';
                          link.style.display = 'none';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                        } catch (error) {
                          console.error('Download failed, opening in new tab:', error);
                          window.open(
                            'https://innexpublic.blob.core.windows.net/platform-assets/onboarding/Waste Policy.pdf',
                            '_blank'
                          );
                        }
                      }}
                      variant="primary"
                      size="sm"
                    >
                      Waste Policy.pdf
                    </Button>
                  </div>
                ) : undefined}
              </Step>

              <Step
                title="Upload Policy and Proceed"
                state={steps.upload}
                onTitleClick={() => {
                  setActiveStep('upload');
                }}
              >
                {steps.upload === 'loading' ? (
                  <div className="flex flex-col gap-2">
                    <div className="text-sm text-content-subtle">
                      1. Start a new Policy review. <br /> 2. Upload the sample policy. <br /> 3. Click "Proceed" and
                      wait for the review to start.
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setSteps((s) => ({ ...s, upload: 'complete', review: 'loading' }));
                      }}
                    >
                      I uploaded and proceeded
                    </Button>
                  </div>
                ) : undefined}
              </Step>

              <Step
                title="Review Policy"
                state={steps.review}
                onTitleClick={() => {
                  try {
                    localStorage.removeItem('innex-review-start-clicked');
                  } catch {}
                  setActiveStep('review');
                }}
              >
                {steps.review === 'loading' ? (
                  <div className="text-sm text-content-subtle">
                    On this page, you can choose what to review the policy against. <br /> <br />
                    Click on "Start Review" to begin the review.
                  </div>
                ) : undefined}
              </Step>

              <Step
                title="Inspect Errors"
                state={steps.inspect}
                isLast={!allComplete}
                onTitleClick={() => {
                  try {
                    localStorage.removeItem('innex-review-inspected-errors');
                  } catch {}
                  setActiveStep('inspect');
                }}
              >
                {steps.inspect === 'loading' ? (
                  <div className="text-sm text-content-subtle">
                    Click on a finding card and to inspect the error and copy the suggested fix.
                  </div>
                ) : undefined}
              </Step>

              {allComplete && (
                <Step
                  title="Congrats, you're all set!"
                  state="loading"
                  isLast={true}
                  icon={<ConfettiIcon size={28} className="text-secondary-blue" />}
                >
                  <div className="text-sm text-content-subtle">
                    You're one step closer to getting the most out of CompliMind and saving yourself hours at work!
                  </div>
                </Step>
              )}
            </>
          );
        })()}
      </div>
    </RightPanelShell>
  );
}
